<template>
    <div class="sso-entry">
        <v-progress-circular v-if="!error" indeterminate size="42" width="3" color="primary" />

        <p v-if="!error" class="sso-entry__text">{{ $t('common.messages.info.openingSession') }}</p>

        <template v-else>
            <span class="material-symbols-outlined sso-entry__icon">lock</span>
            <p class="sso-entry__text">{{ $t('common.messages.errors.ssoFailed') }}</p>
            <v-btn color="primary" variant="flat" @click="goBack">
                {{ $t('common.labels.backToAdmin') }}
            </v-btn>
        </template>
    </div>
</template>

<script setup>
/**
 * Unica porta de entrada do accounts.
 *
 * Recebe o handoff que o admin mandou no fragmento -- ticket (atual) ou token
 * (formato anterior, ver services/sso.js) --, guarda a sessao na origem daqui e
 * reconstroi o estado a partir da API. De proposito nao recebe usuario nem
 * permissoes pelo link: copia de estado nasce velha, e o /me e o catalogo de
 * permissoes ja sao a fonte de verdade.
 */
import { onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { setAuth, setUser, exchangeTicket } from '@/services/api/auth.js';
import { useAuthStore } from '@/stores/authStore.js';
import { readHandoff, clearFragment, clearReturnAttempts, backToAdmin, DEFAULT_DESTINATION } from '@/services/sso.js';

const router = useRouter();
const authStore = useAuthStore();
const error = ref(false);

function goBack() {
    backToAdmin({ reason: 'sessao-invalida' });
}

onMounted(async () => {
    const handoff = readHandoff();

    if (!handoff) {
        // Chegou em /sso sem token: ou o link veio quebrado, ou alguem digitou
        // a URL na mao. Quem sabe resolver isso e o admin.
        if (!backToAdmin({ reason: 'sem-token' })) error.value = true;
        return;
    }

    try {
        let destination = handoff.destination;

        if (handoff.type === 'ticket') {
            // O ticket sozinho nao abre nada: quem vale e o token que volta da
            // troca, ja emitido para esta aplicacao.
            const session = await exchangeTicket(handoff.ticket);

            setAuth({ token: session.access_token });

            if (session.credentials?.corp) {
                localStorage.setItem('corp', session.credentials.corp);
            }

            destination = session.redirect_to || destination;
        } else {
            setAuth({ token: handoff.token });

            if (handoff.corp) {
                localStorage.setItem('corp', handoff.corp);
            }
        }

        clearFragment();

        await setUser();

        // A sessao vale pelo /me. Falhar ao carregar permissoes nao pode
        // derrubar a entrada: sem arvore o usuario ve menos telas, mas com a
        // sessao descartada ele nao ve nenhuma -- e nem consegue entrar para
        // resolver o que faltou.
        try {
            await authStore.fetchPermissions();
        } catch (e) {
            console.error('Nao foi possivel carregar as permissoes:', e);
        }

        authStore.scheduleTokenRefresh();

        // Entrou: o contador que protege contra pingue-pongue com o admin zera,
        // senao a terceira sessao legitima desta aba viria sem pedido de volta.
        clearReturnAttempts();

        await router.replace(destination || DEFAULT_DESTINATION);
    } catch (e) {
        console.error('Handoff de sessao recusado:', e);
        localStorage.clear();
        clearFragment();

        if (!backToAdmin({ reason: 'sessao-invalida' })) error.value = true;
    }
});
</script>

<style scoped lang="scss">
.sso-entry {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;

    &__text {
        color: var(--secondaryColor);
        margin: 0;
    }

    &__icon {
        font-size: 40px;
        color: var(--secondaryColor);
    }
}
</style>
